import { useState } from 'react'
import { Plus, Edit, Eye, Monitor, Save, X, TrendingUp, MousePointer } from 'lucide-react'
import { Button, Badge, Card, CardBody, Modal, Input, Select } from '../../components/ui'
import type { AdSlot } from '../../types'

// Mock data
const mockAdSlots: AdSlot[] = [
  {
    id: 'slot-1',
    name: 'メインバナー',
    slotType: 'main_banner',
    width: 728,
    height: 280,
    description: 'DTL Proランチャー中央上部のメインバナー枠',
    status: 'active',
    createdAt: '2024-01-01',
  },
  {
    id: 'slot-2',
    name: '正方形バナー',
    slotType: 'square_banner',
    width: 300,
    height: 250,
    description: 'DTL Proランチャー右側の正方形バナー枠',
    status: 'active',
    createdAt: '2024-01-01',
  },
  {
    id: 'slot-3',
    name: 'アイコンバナー1',
    slotType: 'icon_banner',
    width: 64,
    height: 64,
    description: 'アイコン列1番目',
    status: 'active',
    createdAt: '2024-01-01',
  },
  {
    id: 'slot-4',
    name: 'アイコンバナー2',
    slotType: 'icon_banner',
    width: 64,
    height: 64,
    description: 'アイコン列2番目',
    status: 'active',
    createdAt: '2024-01-01',
  },
  {
    id: 'slot-5',
    name: 'アイコンバナー3',
    slotType: 'icon_banner',
    width: 64,
    height: 64,
    description: 'アイコン列3番目',
    status: 'inactive',
    createdAt: '2024-01-01',
  },
]

// Mock stats
const slotStats: Record<string, { campaigns: number; impressions: number; clicks: number; ctr: number }> = {
  'slot-1': { campaigns: 12, impressions: 234567, clicks: 8765, ctr: 3.74 },
  'slot-2': { campaigns: 8, impressions: 156789, clicks: 5432, ctr: 3.46 },
  'slot-3': { campaigns: 3, impressions: 45678, clicks: 1234, ctr: 2.70 },
  'slot-4': { campaigns: 2, impressions: 34567, clicks: 987, ctr: 2.86 },
  'slot-5': { campaigns: 0, impressions: 0, clicks: 0, ctr: 0 },
}

// Mock delivery status
const deliveryStatus: Record<string, { campaignName: string; impressions: number; clicks: number }[]> = {
  'slot-1': [
    { campaignName: '春のゲーム祭り', impressions: 89012, clicks: 3456 },
    { campaignName: '新作タイトルプロモ', impressions: 67890, clicks: 2345 },
    { campaignName: 'カフェキャンペーン', impressions: 45678, clicks: 1890 },
  ],
  'slot-2': [
    { campaignName: '春のゲーム祭り', impressions: 56789, clicks: 2134 },
    { campaignName: '年末年始セール', impressions: 34567, clicks: 1234 },
  ],
  'slot-3': [
    { campaignName: 'アイコン広告A', impressions: 23456, clicks: 678 },
  ],
  'slot-4': [
    { campaignName: 'アイコン広告B', impressions: 18765, clicks: 543 },
  ],
  'slot-5': [],
}

export default function AdSlotListPage() {
  const [selectedSlot, setSelectedSlot] = useState<AdSlot | null>(null)
  const [showPreview, setShowPreview] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showStatusModal, setShowStatusModal] = useState(false)
  const [editingSlot, setEditingSlot] = useState<AdSlot | null>(null)
  const [showCreateModal, setShowCreateModal] = useState(false)

  const handleEdit = (slot: AdSlot, e: React.MouseEvent) => {
    e.stopPropagation()
    setEditingSlot(slot)
    setShowEditModal(true)
  }

  const handleShowStatus = (slot: AdSlot, e: React.MouseEvent) => {
    e.stopPropagation()
    setEditingSlot(slot)
    setShowStatusModal(true)
  }

  const handleSaveEdit = () => {
    // In real app, call API to save
    alert('広告枠を保存しました')
    setShowEditModal(false)
    setEditingSlot(null)
  }

  const handleCreate = () => {
    // In real app, call API to create
    alert('広告枠を作成しました')
    setShowCreateModal(false)
  }

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">広告枠管理</h1>
          <p className="text-sm text-gray-500 mt-1">DTL Proランチャーの広告枠設定</p>
        </div>
        <div className="flex gap-3">
          <Button variant="secondary" leftIcon={<Monitor className="w-4 h-4" />} onClick={() => setShowPreview(true)}>
            プレビュー
          </Button>
          <Button leftIcon={<Plus className="w-4 h-4" />} onClick={() => setShowCreateModal(true)}>
            新規広告枠
          </Button>
        </div>
      </div>

      {/* Ad Slots Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {mockAdSlots.map((slot) => {
          const stats = slotStats[slot.id]
          return (
            <div
              key={slot.id}
              className={`cursor-pointer transition-all hover:shadow-md rounded-lg ${selectedSlot?.id === slot.id ? 'ring-2 ring-primary-500' : ''}`}
              onClick={() => setSelectedSlot(slot)}
            >
            <Card>
              <CardBody>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-semibold text-gray-900">{slot.name}</h3>
                    <p className="text-sm text-gray-500">{slot.description}</p>
                  </div>
                  <Badge variant={slot.status === 'active' ? 'success' : 'gray'} dot>
                    {slot.status === 'active' ? '有効' : '無効'}
                  </Badge>
                </div>

                {/* Size Preview */}
                <div className="bg-gray-100 rounded-lg p-4 mb-4 flex items-center justify-center">
                  <div 
                    className="bg-primary-100 border-2 border-dashed border-primary-300 rounded flex items-center justify-center text-primary-600 text-xs font-medium"
                    style={{ 
                      width: Math.min(slot.width / 3, 200), 
                      height: Math.min(slot.height / 3, 100),
                      minWidth: 60,
                      minHeight: 40
                    }}
                  >
                    {slot.width} x {slot.height}
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">配信中キャンペーン</p>
                    <p className="font-semibold text-gray-900">{stats.campaigns}件</p>
                  </div>
                  <div>
                    <p className="text-gray-500">本日IMP</p>
                    <p className="font-semibold text-gray-900">{stats.impressions.toLocaleString()}</p>
                  </div>
                </div>

                {/* Actions */}
                <div className="mt-4 pt-4 border-t border-gray-100 flex gap-2">
                  <Button 
                    size="sm" 
                    variant="secondary" 
                    leftIcon={<Edit className="w-3 h-3" />}
                    onClick={(e) => handleEdit(slot, e)}
                  >
                    編集
                  </Button>
                  <Button 
                    size="sm" 
                    variant="secondary" 
                    leftIcon={<Eye className="w-3 h-3" />}
                    onClick={(e) => handleShowStatus(slot, e)}
                  >
                    配信状況
                  </Button>
                </div>
              </CardBody>
            </Card>
            </div>
          )
        })}
      </div>

      {/* Edit Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false)
          setEditingSlot(null)
        }}
        title={`広告枠を編集: ${editingSlot?.name || ''}`}
        size="md"
      >
        {editingSlot && (
          <form onSubmit={(e) => { e.preventDefault(); handleSaveEdit(); }} className="space-y-4">
            <Input
              label="広告枠名"
              defaultValue={editingSlot.name}
              required
            />
            <Select
              label="枠タイプ"
              defaultValue={editingSlot.slotType}
              options={[
                { value: 'main_banner', label: 'メインバナー' },
                { value: 'square_banner', label: '正方形バナー' },
                { value: 'icon_banner', label: 'アイコンバナー' },
              ]}
            />
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="幅 (px)"
                type="number"
                defaultValue={editingSlot.width}
                required
              />
              <Input
                label="高さ (px)"
                type="number"
                defaultValue={editingSlot.height}
                required
              />
            </div>
            <div>
              <label className="form-label">説明</label>
              <textarea
                className="form-textarea"
                rows={3}
                defaultValue={editingSlot.description}
              />
            </div>
            <Select
              label="ステータス"
              defaultValue={editingSlot.status}
              options={[
                { value: 'active', label: '有効' },
                { value: 'inactive', label: '無効' },
              ]}
            />
            <Input
              label="デフォルト広告URL（空白時に表示）"
              type="url"
              placeholder="https://example.com/default-banner.jpg"
            />
            <div className="flex justify-end gap-3 pt-4">
              <Button 
                type="button" 
                variant="secondary" 
                leftIcon={<X className="w-4 h-4" />}
                onClick={() => {
                  setShowEditModal(false)
                  setEditingSlot(null)
                }}
              >
                キャンセル
              </Button>
              <Button type="submit" leftIcon={<Save className="w-4 h-4" />}>
                保存
              </Button>
            </div>
          </form>
        )}
      </Modal>

      {/* Delivery Status Modal */}
      <Modal
        isOpen={showStatusModal}
        onClose={() => {
          setShowStatusModal(false)
          setEditingSlot(null)
        }}
        title={`配信状況: ${editingSlot?.name || ''}`}
        size="lg"
      >
        {editingSlot && (
          <div className="space-y-6">
            {/* Summary Stats */}
            <div className="grid grid-cols-4 gap-4">
              <div className="bg-blue-50 rounded-lg p-4 text-center">
                <TrendingUp className="w-6 h-6 text-blue-500 mx-auto mb-2" />
                <p className="text-2xl font-bold text-gray-900">{slotStats[editingSlot.id].impressions.toLocaleString()}</p>
                <p className="text-sm text-gray-500">インプレッション</p>
              </div>
              <div className="bg-green-50 rounded-lg p-4 text-center">
                <MousePointer className="w-6 h-6 text-green-500 mx-auto mb-2" />
                <p className="text-2xl font-bold text-gray-900">{slotStats[editingSlot.id].clicks.toLocaleString()}</p>
                <p className="text-sm text-gray-500">クリック</p>
              </div>
              <div className="bg-yellow-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-gray-900 mt-2">{slotStats[editingSlot.id].ctr}%</p>
                <p className="text-sm text-gray-500 mt-2">CTR</p>
              </div>
              <div className="bg-purple-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-gray-900 mt-2">{slotStats[editingSlot.id].campaigns}</p>
                <p className="text-sm text-gray-500 mt-2">配信中</p>
              </div>
            </div>

            {/* Campaign List */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-3">配信中のキャンペーン</h4>
              {deliveryStatus[editingSlot.id].length > 0 ? (
                <div className="border border-gray-200 rounded-lg divide-y divide-gray-200">
                  {deliveryStatus[editingSlot.id].map((campaign, index) => (
                    <div key={index} className="p-4 flex items-center justify-between hover:bg-gray-50">
                      <div>
                        <p className="font-medium text-gray-900">{campaign.campaignName}</p>
                        <p className="text-sm text-gray-500">
                          IMP: {campaign.impressions.toLocaleString()} / Click: {campaign.clicks.toLocaleString()}
                        </p>
                      </div>
                      <Badge variant="success" dot>配信中</Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                  <p className="text-gray-500">現在配信中のキャンペーンはありません</p>
                </div>
              )}
            </div>

            <div className="flex justify-end">
              <Button 
                variant="secondary"
                onClick={() => {
                  setShowStatusModal(false)
                  setEditingSlot(null)
                }}
              >
                閉じる
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Create Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="新規広告枠作成"
        size="md"
      >
        <form onSubmit={(e) => { e.preventDefault(); handleCreate(); }} className="space-y-4">
          <Input
            label="広告枠名"
            placeholder="メインバナー"
            required
          />
          <Select
            label="枠タイプ"
            options={[
              { value: 'main_banner', label: 'メインバナー' },
              { value: 'square_banner', label: '正方形バナー' },
              { value: 'icon_banner', label: 'アイコンバナー' },
            ]}
            placeholder="タイプを選択"
            required
          />
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="幅 (px)"
              type="number"
              placeholder="728"
              required
            />
            <Input
              label="高さ (px)"
              type="number"
              placeholder="280"
              required
            />
          </div>
          <div>
            <label className="form-label">説明</label>
            <textarea
              className="form-textarea"
              rows={3}
              placeholder="広告枠の説明を入力"
            />
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <Button 
              type="button" 
              variant="secondary"
              onClick={() => setShowCreateModal(false)}
            >
              キャンセル
            </Button>
            <Button type="submit" leftIcon={<Plus className="w-4 h-4" />}>
              作成
            </Button>
          </div>
        </form>
      </Modal>

      {/* Launcher Preview Modal */}
      <Modal
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        title="DTL Pro ランチャー プレビュー"
        size="xl"
      >
        <div className="bg-gray-800 rounded-lg p-4">
          {/* Simulated Launcher UI */}
          <div className="bg-white rounded-lg p-4 space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-xl">🎮</span>
                <span className="font-bold text-gray-800">DTL Pro</span>
              </div>
              <div className="flex gap-2 text-sm text-gray-500">
                <span>ようこそ</span>
              </div>
            </div>

            {/* Main Content */}
            <div className="flex gap-4">
              {/* Left Sidebar */}
              <div className="w-32 space-y-2 text-sm">
                <div className="p-2 bg-blue-50 text-blue-600 rounded">オンラインゲーム</div>
                <div className="p-2 hover:bg-gray-50 rounded">無料動画</div>
                <div className="p-2 hover:bg-gray-50 rounded">シネマ情報</div>
                <div className="p-2 hover:bg-gray-50 rounded">新作ゲーム</div>
              </div>

              {/* Main Banner Area */}
              <div className="flex-1">
                <div className="bg-gradient-to-r from-amber-100 to-amber-50 rounded-lg p-6 h-32 flex items-center justify-center border-2 border-dashed border-amber-300">
                  <span className="text-amber-600 font-medium">メインバナー (728x280)</span>
                </div>
              </div>

              {/* Right Sidebar */}
              <div className="w-40">
                <div className="bg-gradient-to-r from-purple-100 to-purple-50 rounded-lg p-4 h-32 flex items-center justify-center border-2 border-dashed border-purple-300">
                  <span className="text-purple-600 font-medium text-sm text-center">正方形バナー<br/>(300x250)</span>
                </div>
              </div>
            </div>

            {/* Icon Row */}
            <div className="flex justify-center gap-4">
              {['オススメゲーム', 'コミック閲覧', 'マップ', '音楽ライブ', '映画・アニメ', 'フード注文'].map((label, i) => (
                <div key={i} className="text-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-blue-50 rounded-lg flex items-center justify-center border border-blue-200">
                    <span className="text-xs text-blue-600">64x64</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
        <p className="text-sm text-gray-500 mt-4 text-center">
          各広告枠の位置を視覚的に確認できます
        </p>
      </Modal>
    </div>
  )
}
